import { gql } from '@apollo/client';

export const ChargeableItemFragment = gql`
  fragment ChargeableItemFragment on ChargeableItem {
    id
    name
    description
    shortDescription
    nameI18N
    descriptionI18N
    shortDescriptionI18N
    price
    limit
    config
  }
`;

export const ServiceGroupFragment = gql`
  fragment ServiceGroupFragment on PatientCallGroupItem {
    id
    name
    nameI18N
    modifier
    customizationData
    conditional
    isDefault
    service {
      id
      reason
      reasonI18N
      icons
      modifier
      customizationType
      customizationData
      actions
      conditional
      serviceType
      paymentConfig {
        id
        data
      }

      lastAlert: alerts(
        first: 1
        orderBy: [CREATED_AT_DESC]
        filter: { admissionId: { equalTo: $admissionId } }
      ) {
        nodes {
          id
          createdAt
        }
      }

      activeAlerts: alerts(
        filter: {
          admissionId: { equalTo: $admissionId }
          resolvedAt: { isNull: true }
          cancelledAt: { isNull: true }
        }
      ) {
        nodes {
          id
        }
      }

      chargeableItems: chargeableItemsByServiceChargeableItemServiceIdAndChargeableItemId(
        orderBy: [ORDER_ASC]
        filter: { enabled: { equalTo: true } }
      ) {
        nodes {
          ...ChargeableItemFragment
        }
      }

      serviceCustomFields(
        orderBy: [ORDER_ASC]
        filter: { enabled: { equalTo: true }, when: { equalTo: ON_CREATE } }
      ) {
        nodes {
          id
          serviceId
          order
          label
          labelI18N
          inputType
          placeholder
          placeholderI18N
          required
          data
          when
        }
      }
    }
  }
  ${ChargeableItemFragment}
`;

/**
 * Children sub-tree shared by the single group-item lookups, so the result
 * shape matches `PatientCallReasonQueryResult` and can be fed to `parseGroups`.
 */
const GROUP_ITEM_CHILDREN = `
  children: childPatientCallGroupItems(
    orderBy: ORDER_ASC
    filter: { enabled: { equalTo: true } }
  ) {
    totalCount
    nodes {
      ...ServiceGroupFragment
      children: childPatientCallGroupItems(
        orderBy: ORDER_ASC
        filter: { enabled: { equalTo: true } }
      ) {
        totalCount
        nodes {
          ...ServiceGroupFragment
        }
      }
    }
  }
`;

/**
 * Resolves a single `service_group_item` (patient_call_group_item) by its id,
 * including its children sub-tree (to detect `hasChildren`).
 */
export const FETCH_GROUP_ITEM_QUERY = gql`
  query FetchOrderGroupItem($id: UUID!, $admissionId: UUID!) {
    groups: patientCallGroupItems(
      first: 1
      filter: { id: { equalTo: $id }, enabled: { equalTo: true } }
    ) {
      nodes {
        ...ServiceGroupFragment
        ${GROUP_ITEM_CHILDREN}
      }
    }
  }
  ${ServiceGroupFragment}
`;

/**
 * Resolves the `service_group_item` that wraps a given `service` id. Used by the
 * `SERVICE_BUTTON` flow when only a `serviceId` is configured — we still need a
 * group-item id to populate `alert.groupId`.
 */
export const FETCH_GROUP_ITEM_BY_SERVICE_QUERY = gql`
  query FetchOrderGroupItemByService($serviceId: UUID!, $admissionId: UUID!) {
    groups: patientCallGroupItems(
      first: 1
      filter: {
        service: { id: { equalTo: $serviceId } }
        enabled: { equalTo: true }
      }
    ) {
      nodes {
        ...ServiceGroupFragment
        ${GROUP_ITEM_CHILDREN}
      }
    }
  }
  ${ServiceGroupFragment}
`;

export const FETCH_ADMISSION_COVERAGE = gql`
  query CheckAdmissionCoverageQuery(
    $admissionId: UUID!
    $type: EnumCheckCoverageType!
  ) {
    checkAdmissionCoverage(admissionId: $admissionId, type: $type) {
      admissionId
      status
    }
  }
`;
